import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tasksApi } from "@/features/tasks/api/tasks";
import type { Task, TaskFilters, TaskFormData } from "@/features/tasks/types";

export const taskKeys = {
  all: ["tasks"] as const,
  list: (filters?: TaskFilters) => [...taskKeys.all, "list", filters] as const,
  detail: (id: string) => [...taskKeys.all, "detail", id] as const,
};

export function useTasks(filters?: TaskFilters) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => tasksApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => tasksApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TaskFormData) => tasksApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useUpdateTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TaskFormData>) => tasksApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useAssignUser(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => tasksApi.assign(taskId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    },
  });
}

export function useUnassignUser(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => tasksApi.unassign(taskId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    },
  });
}

export function useCloneTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ task_ids, target_project_id }: { task_ids: string[]; target_project_id: string }) =>
      tasksApi.cloneBulk(task_ids, target_project_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useSetTaskExpanded() {
  return useMutation({
    mutationFn: ({ id, is_expanded }: { id: string; is_expanded: boolean }) =>
      tasksApi.setExpanded(id, is_expanded),
  });
}

export function useTaskGroupStates() {
  return useQuery({
    queryKey: ["tasks", "group-states"] as const,
    queryFn: () => tasksApi.groupStates(),
    staleTime: Infinity,
  });
}

export function useSetGroupCollapsed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_key, is_collapsed }: { project_key: string; is_collapsed: boolean }) =>
      tasksApi.setGroupCollapsed(project_key, is_collapsed),
    onSuccess: (_, { project_key, is_collapsed }) => {
      qc.setQueryData<string[]>(["tasks", "group-states"], (old = []) =>
        is_collapsed
          ? old.includes(project_key) ? old : [...old, project_key]
          : old.filter((k) => k !== project_key),
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["tasks", "group-states"] });
    },
  });
}

export function usePinTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.pin(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: taskKeys.all });
      qc.setQueriesData<Task[]>({ queryKey: taskKeys.all }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        const updated = old.map((t) =>
          t.id === id ? { ...t, is_pinned: !t.is_pinned } : t,
        );
        return [...updated].sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
          return a.position - b.position;
        });
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useArchiveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? tasksApi.archive(id) : tasksApi.unarchive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: ["subtasks"] });
    },
  });
}

export function useReorderTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => tasksApi.reorder(ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: taskKeys.all });
      const posMap = new Map(ids.map((id, i) => [id, i]));
      qc.setQueriesData<Task[]>({ queryKey: taskKeys.all }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return [...old]
          .map((t) => ({ ...t, position: posMap.get(t.id) ?? t.position }))
          .sort((a, b) => a.position - b.position);
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useTaskUIPrefs() {
  return useQuery({
    queryKey: ["tasks", "ui-prefs"] as const,
    queryFn: () => tasksApi.getUIPrefs(),
    staleTime: Infinity,
  });
}

export function useSetTaskUIPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Partial<{ filters_hidden: boolean; kanban_hidden: boolean; table_panel_hidden: boolean }>) =>
      tasksApi.setUIPrefs(prefs),
    onMutate: async (prefs) => {
      await qc.cancelQueries({ queryKey: ["tasks", "ui-prefs"] });
      qc.setQueryData<{ filters_hidden: boolean; kanban_hidden: boolean; table_panel_hidden: boolean }>(
        ["tasks", "ui-prefs"],
        (old) => old ? { ...old, ...prefs } : { filters_hidden: false, kanban_hidden: false, table_panel_hidden: false, ...prefs },
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["tasks", "ui-prefs"] });
    },
  });
}

export function useWatchTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (watching: boolean) =>
      watching ? tasksApi.watch(taskId) : tasksApi.unwatch(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.list() });
      qc.invalidateQueries({ queryKey: ["subtasks", taskId] });
    },
  });
}
